/**
 * 커스텀 아바타 동적 설정 생성기
 *
 * DB에서 로드한 커스텀 아바타 데이터를 기반으로
 * Three.js에서 사용할 수 있는 AvatarConfig와 DefaultConfig를 생성합니다.
 */

import { defaultConfig } from './default.config'
import { SmartScenarioBuilders } from '../avatar/common/player/util'
import type { AvatarConfig, LipsyncConfig, RangeMappingConfig, FilterConfig, VrmFilterConfig } from '../avatar/common/player/type'
import type { ResourceType, LoaderConfig } from '../base/loader/type'
import type { CameraConfig } from '../base/camera/type'
import type { LightingConfig, LightConfigType } from '../base/light/type'
import type { RendererConfig } from '../base/renderer/type'
import type { ScenarioConfigs } from '../avatar/common/animation/type'

// 등신별 기본 애니메이션 리소스 및 설정 (모델 제외)
import { nalnaniResources, nalnaniAvatarConfig, nalnaniConfig } from './nalnani.config'
import { glyxResources, glyxAvatarConfig, glyxConfig } from './glyx.config'
import { kikizResources, kikizAvatarConfig, kikizConfig } from './kikiz.config'

// 등신별 기본 리소스 매핑 (애니메이션만 가져옴, 모델 제외)
const BODY_TYPE_DEFAULT_RESOURCES: Record<string, ResourceType[]> = {
  '2head': nalnaniResources.filter(r => r.type === 'animation_only'), // 2등신은 nalnani 애니메이션 사용
  '3head': nalnaniResources.filter(r => r.type === 'animation_only'),
  '5head': kikizResources.filter(r => r.type === 'animation_only'),
  '8head': glyxResources.filter(r => r.type === 'animation_only'),
}

// 등신별 기본 AvatarConfig 매핑 (scenarioConfigs 가져오기 위함)
const BODY_TYPE_DEFAULT_AVATAR_CONFIGS: Record<string, AvatarConfig> = {
  '2head': nalnaniAvatarConfig,
  '3head': nalnaniAvatarConfig,
  '5head': kikizAvatarConfig,
  '8head': glyxAvatarConfig,
}

// 커스텀 아바타 데이터 타입 (프론트엔드에서 사용)
export interface CustomAvatarData {
  id: string
  name: string
  bodyType: '2head' | '3head' | '5head' | '8head'
  modelUrl: string
  thumbnailUrl?: string
  cameraConfig: {
    fov: number
    position: { x: number; y: number; z: number }
    rotation: { x: number; y: number; z: number }
    near: number
    far: number
  }
  lightingConfig: {
    lights: Array<{
      type: 'DirectionalLight' | 'HemisphereLight' | 'PointLight' | 'AmbientLight'
      color: number
      intensity: number
      position?: { x: number; y: number; z: number }
      castShadow?: boolean
      skyColor?: number
      groundColor?: number
    }>
    environmentHdr: string
  }
  lipsyncConfig: {
    indices: number[]
    processingType: 'filter' | 'range_mapping' | 'vrm_filter'
    config: Record<string, unknown>
  }
  animations: Array<{
    id: string
    name: string
    scenarioType: string
    animationUrl: string
    playMode: 'once' | 'loop' | 'random' | 'last_loop'
    blendDuration: number
  }>
  scale?: { x: number; y: number; z: number }
  position?: { x: number; y: number; z: number }
  rotation?: { x: number; y: number; z: number }
}

// ============ 시나리오 라이브러리 기반 확장 타입 ============

/**
 * 시나리오 설정 애니메이션 아이템
 */
export interface ScenarioAnimationItem {
  animationId: string     // animation_library ID 또는 'default_xxx'
  isDefault: boolean      // 기본 애니메이션 여부
  order: number
}

/**
 * 시나리오 설정 (DB에서 로드)
 */
export interface ScenarioConfigurationData {
  id: string
  scenarioType: string
  animations: ScenarioAnimationItem[]
  playMode: 'once' | 'loop' | 'random' | 'last_loop'
  blendDuration: number
}

/**
 * 애니메이션 라이브러리 아이템 (프리뷰 및 리소스 생성용)
 */
export interface AnimationLibraryItemData {
  id: string
  name: string
  bodyType: string
  animationUrl: string
}

/**
 * 시나리오 설정 기반 커스텀 아바타 데이터 (확장 타입)
 */
export interface CustomAvatarDataWithScenarios extends Omit<CustomAvatarData, 'animations'> {
  // 기존 animations 필드 (레거시 호환)
  animations: CustomAvatarData['animations']
  // 새로운 시나리오 설정 (우선 적용)
  scenarioConfigs?: ScenarioConfigurationData[]
  // 애니메이션 라이브러리 (시나리오 설정에서 참조)
  animationLibrary?: AnimationLibraryItemData[]
}

// 등신별 기본 스케일
const BODY_TYPE_SCALES: Record<string, { x: number; y: number; z: number }> = {
  '2head': { x: 25, y: 25, z: 25 },
  '3head': { x: 25, y: 25, z: 25 },
  '5head': { x: 1, y: 1, z: 1 },
  '8head': { x: 1, y: 1, z: 1 },
}

// 등신별 기본 시나리오
const BODY_TYPE_DEFAULT_SCENARIOS: Record<string, string[]> = {
  '2head': ['entrance', 'waiting', 'speaking'],
  '3head': ['entrance', 'start', 'waiting', 'listening', 'processing', 'speaking', 'error', 'toss'],
  '5head': ['entrance', 'waiting', 'listening', 'speaking'],
  '8head': ['entrance', 'listening', 'processing', 'waiting', 'speaking', 'error'],
}

/**
 * 커스텀 아바타 리소스 배열 생성
 * - 하이브리드 방식: 기본 등신별 애니메이션 + 커스텀 애니메이션 병합
 * - 커스텀 애니메이션이 있으면 해당 시나리오만 오버라이드
 */
function createCustomResources(data: CustomAvatarData): ResourceType[] {
  const resources: ResourceType[] = [
    {
      name: 'custom_model',
      type: 'model_only',
      path: data.modelUrl,
      format: 'glb',
    },
  ]

  // 1. 먼저 등신별 기본 애니메이션 리소스 추가 (항상)
  const defaultAnimResources = BODY_TYPE_DEFAULT_RESOURCES[data.bodyType]
  if (defaultAnimResources) {
    console.log(`[CustomAvatar] ${data.bodyType} 기본 애니메이션 로드`)
    resources.push(...defaultAnimResources)
  }

  // 2. 커스텀 애니메이션이 있으면 추가 (하이브리드)
  if (data.animations && data.animations.length > 0) {
    console.log(`[CustomAvatar] 커스텀 애니메이션 ${data.animations.length}개 추가`)
    for (const anim of data.animations) {
      resources.push({
        name: `custom_${anim.name}`, // 커스텀 접두사로 구분
        type: 'animation_only',
        path: anim.animationUrl,
        format: 'glb',
      })
    }
  }

  return resources
}

/**
 * 시나리오별 애니메이션 그룹화
 */
function groupAnimationsByScenario(
  animations: CustomAvatarData['animations']
): Record<string, CustomAvatarData['animations']> {
  const grouped: Record<string, CustomAvatarData['animations']> = {}

  for (const anim of animations) {
    if (!grouped[anim.scenarioType]) {
      grouped[anim.scenarioType] = []
    }
    grouped[anim.scenarioType].push(anim)
  }

  return grouped
}

/**
 * 커스텀 시나리오 설정 생성
 * - 하이브리드 방식: 기본 시나리오를 베이스로 하고, 커스텀 애니메이션이 있는 시나리오만 오버라이드
 */
function createCustomScenarios(
  data: CustomAvatarData,
  resources: ResourceType[]
): ScenarioConfigs<string> {
  // 1. 기본 시나리오를 베이스로 복사
  const defaultAvatarConfig = BODY_TYPE_DEFAULT_AVATAR_CONFIGS[data.bodyType]
  const scenarios: ScenarioConfigs<string> = defaultAvatarConfig?.scenarioConfigs
    ? { ...defaultAvatarConfig.scenarioConfigs }
    : {}

  console.log(`[CustomAvatar] ${data.bodyType} 기본 시나리오 로드:`, Object.keys(scenarios))

  // 커스텀 애니메이션이 없으면 기본 시나리오만 반환
  if (!data.animations || data.animations.length === 0) {
    return scenarios
  }

  // 2. 커스텀 애니메이션으로 특정 시나리오 오버라이드
  const groupedAnimations = groupAnimationsByScenario(data.animations)

  // PlayMode 변환 함수
  const mapPlayMode = (mode: string): 'once' | 'random_loop' | 'last_loop' | 'sequential_loop' => {
    switch (mode) {
      case 'once': return 'once'
      case 'loop': return 'sequential_loop'
      case 'random': return 'random_loop'
      case 'last_loop': return 'last_loop'
      default: return 'once'
    }
  }

  // 커스텀 애니메이션으로 해당 시나리오 오버라이드
  for (const [scenarioType, anims] of Object.entries(groupedAnimations)) {
    // 커스텀 애니메이션 이름에 'custom_' 접두사 추가
    const customAnimNames = anims.map(a => `custom_${a.name}`)

    if (anims.length === 1) {
      const anim = anims[0]
      scenarios[scenarioType] = {
        animations: customAnimNames,
        playMode: mapPlayMode(anim.playMode),
        blendDuration: anim.blendDuration,
        description: `커스텀 ${scenarioType} 애니메이션 (오버라이드)`,
      }
    } else {
      const primaryMode = anims[0].playMode
      scenarios[scenarioType] = {
        animations: customAnimNames,
        playMode: mapPlayMode(primaryMode),
        blendDuration: anims[0].blendDuration,
        description: `커스텀 ${scenarioType} 애니메이션 (오버라이드)`,
      }
    }

    console.log(`[CustomAvatar] 시나리오 '${scenarioType}' 커스텀으로 오버라이드:`, customAnimNames)
  }

  return scenarios
}

/**
 * 커스텀 아바타 AvatarConfig 생성
 * - lipsync 설정이 없으면 등신별 기본 lipsync 사용
 */
export function createCustomAvatarConfig(data: CustomAvatarData): AvatarConfig {
  const resources = createCustomResources(data)
  const scenarios = createCustomScenarios(data, resources)
  const scale = data.scale || BODY_TYPE_SCALES[data.bodyType] || { x: 1, y: 1, z: 1 }

  // lipsync 설정 결정 - 커스텀 설정이 없으면 기본값 사용
  let lipsync: LipsyncConfig

  const hasCustomLipsync = data.lipsyncConfig?.indices && data.lipsyncConfig.indices.length > 0

  if (hasCustomLipsync) {
    // 커스텀 lipsync 설정 사용
    const lipsyncConfigDetail = data.lipsyncConfig.config as unknown as
      | RangeMappingConfig
      | FilterConfig
      | VrmFilterConfig
      | undefined

    lipsync = {
      indices: data.lipsyncConfig.indices,
      processingType: data.lipsyncConfig.processingType,
      config: lipsyncConfigDetail,
    }
  } else {
    // 등신별 기본 lipsync 사용
    const defaultAvatarConfig = BODY_TYPE_DEFAULT_AVATAR_CONFIGS[data.bodyType]
    if (defaultAvatarConfig?.lipsync) {
      console.log(`[CustomAvatar] 커스텀 lipsync 없음 - ${data.bodyType} 기본 lipsync 사용`)
      lipsync = defaultAvatarConfig.lipsync
    } else {
      // 최종 fallback
      lipsync = {
        indices: [],
        processingType: 'filter',
        config: { indices: [] },
      }
    }
  }

  return {
    type: 'custom' as const,
    modelKey: 'custom_model',
    resources,
    scenarioConfigs: scenarios,
    scale,
    position: data.position,
    rotation: data.rotation,
    lipsync,
  }
}

/**
 * 커스텀 카메라 설정 생성
 */
function createCustomCameraConfig(data: CustomAvatarData): CameraConfig {
  return {
    ...defaultConfig.camera,
    cameras: [
      {
        name: 'custom_main_camera',
        type: 'PerspectiveCamera',
        active: true,
        fov: data.cameraConfig.fov,
        position: data.cameraConfig.position,
        rotation: data.cameraConfig.rotation,
        near: data.cameraConfig.near,
        far: data.cameraConfig.far,
      },
    ],
  }
}

/**
 * 커스텀 조명 설정 생성
 */
function createCustomLightingConfig(data: CustomAvatarData): LightingConfig {
  if (!data.lightingConfig.lights || data.lightingConfig.lights.length === 0) {
    return defaultConfig.lighting
  }

  const lights = data.lightingConfig.lights.map(light => {
    const baseLight: LightConfigType = {
      type: light.type,
      color: light.color,
      intensity: light.intensity,
    } as LightConfigType

    if (light.position) {
      (baseLight as { position?: { x: number; y: number; z: number } }).position = light.position
    }

    if (light.castShadow !== undefined) {
      (baseLight as { castShadow?: boolean }).castShadow = light.castShadow
      if (light.castShadow) {
        (baseLight as { shadow?: unknown }).shadow = {
          mapSize: { width: 1024, height: 1024 },
          radius: 6,
          autoUpdate: true,
        }
      }
    }

    // HemisphereLight 전용
    if (light.type === 'HemisphereLight') {
      (baseLight as { skyColor?: number }).skyColor = light.skyColor || light.color;
      (baseLight as { groundColor?: number }).groundColor = light.groundColor || 0xd7d0d0
    }

    return baseLight
  })

  return {
    ...defaultConfig.lighting,
    lights,
    environment: {
      loader: data.lightingConfig.environmentHdr.endsWith('.exr') ? 'EXR' : 'HDR',
      path: data.lightingConfig.environmentHdr,
    },
  }
}

/**
 * 커스텀 아바타 전체 Three.js 설정 생성
 */
export function createCustomAvatarThreeConfig(data: CustomAvatarData): {
  loader: LoaderConfig
  camera: CameraConfig
  lighting: LightingConfig
  renderer: RendererConfig
  avatar: AvatarConfig
} {
  return {
    loader: defaultConfig.loader,
    camera: createCustomCameraConfig(data),
    lighting: createCustomLightingConfig(data),
    renderer: {
      ...defaultConfig.renderer,
      bloom: undefined, // 커스텀 아바타는 기본적으로 bloom 비활성화
    },
    avatar: createCustomAvatarConfig(data),
  }
}

/**
 * CustomAvatar 응답을 CustomAvatarData로 변환
 */
export function transformToCustomAvatarData(avatar: {
  id: string
  name: string
  bodyType: string
  modelUrl: string
  thumbnailUrl?: string
  cameraConfig: Record<string, unknown>
  lightingConfig: Record<string, unknown>
  lipsyncConfig: Record<string, unknown>
  animations: Array<Record<string, unknown>>
  avatarTransform?: {
    scale: { x: number; y: number; z: number }
    position: { x: number; y: number; z: number }
    rotation: { x: number; y: number; z: number }
  }
}): CustomAvatarData {
  return {
    id: avatar.id,
    name: avatar.name,
    bodyType: avatar.bodyType as CustomAvatarData['bodyType'],
    modelUrl: avatar.modelUrl,
    thumbnailUrl: avatar.thumbnailUrl,
    cameraConfig: {
      fov: (avatar.cameraConfig.fov as number) || 40,
      position: (avatar.cameraConfig.position as { x: number; y: number; z: number }) || { x: 0, y: 10, z: 36 },
      rotation: (avatar.cameraConfig.rotation as { x: number; y: number; z: number }) || { x: 0, y: 0, z: 0 },
      near: (avatar.cameraConfig.near as number) || 0.1,
      far: (avatar.cameraConfig.far as number) || 200,
    },
    lightingConfig: {
      lights: ((avatar.lightingConfig.lights as Array<Record<string, unknown>>) || []).map(light => ({
        type: light.type as 'DirectionalLight' | 'HemisphereLight' | 'PointLight' | 'AmbientLight',
        color: (light.color as number) || 0xffffff,
        intensity: (light.intensity as number) || 1,
        position: light.position as { x: number; y: number; z: number } | undefined,
        castShadow: light.castShadow as boolean | undefined,
        skyColor: light.skyColor as number | undefined,
        groundColor: light.groundColor as number | undefined,
      })),
      environmentHdr: (avatar.lightingConfig.environmentHdr as string) || '/hdr/panoramalow.hdr',
    },
    lipsyncConfig: {
      indices: (avatar.lipsyncConfig.indices as number[]) || [],
      processingType: (avatar.lipsyncConfig.processingType as 'filter' | 'range_mapping' | 'vrm_filter') || 'filter',
      config: (avatar.lipsyncConfig.config as Record<string, unknown>) || {},
    },
    animations: (avatar.animations || []).map(anim => ({
      id: anim.id as string,
      name: anim.name as string,
      scenarioType: anim.scenarioType as string,
      animationUrl: anim.animationUrl as string,
      playMode: (anim.playMode as 'once' | 'loop' | 'random' | 'last_loop') || 'once',
      blendDuration: (anim.blendDuration as number) || 0.2,
    })),
    scale: avatar.avatarTransform?.scale,
    position: avatar.avatarTransform?.position,
    rotation: avatar.avatarTransform?.rotation,
  }
}

// ============ 시나리오 라이브러리 기반 함수들 ============

/**
 * 시나리오 설정에서 참조하는 애니메이션 라이브러리 리소스 생성
 */
function createLibraryAnimationResources(
  scenarioConfigs: ScenarioConfigurationData[],
  animationLibrary: AnimationLibraryItemData[]
): ResourceType[] {
  const resources: ResourceType[] = []
  const addedIds = new Set<string>()

  for (const config of scenarioConfigs) {
    for (const animItem of config.animations) {
      // 기본 애니메이션은 스킵 (등신별 기본 리소스에서 로드됨)
      if (animItem.isDefault) continue

      // 이미 추가된 애니메이션은 스킵
      if (addedIds.has(animItem.animationId)) continue

      // 라이브러리에서 애니메이션 찾기
      const libraryAnim = animationLibrary.find(a => a.id === animItem.animationId)
      if (libraryAnim) {
        resources.push({
          name: `lib_${libraryAnim.id}`,
          type: 'animation_only',
          path: libraryAnim.animationUrl,
          format: 'glb',
        })
        addedIds.add(animItem.animationId)
      }
    }
  }

  return resources
}

/**
 * 시나리오 설정 기반 ScenarioConfigs 생성
 * - 시나리오 설정이 있으면 우선 사용
 * - 없으면 기본 시나리오 사용
 */
function createScenariosFromConfigs(
  bodyType: string,
  scenarioConfigs: ScenarioConfigurationData[],
  animationLibrary: AnimationLibraryItemData[]
): ScenarioConfigs<string> {
  // 1. 기본 시나리오를 베이스로 복사
  const defaultAvatarConfig = BODY_TYPE_DEFAULT_AVATAR_CONFIGS[bodyType]
  const scenarios: ScenarioConfigs<string> = defaultAvatarConfig?.scenarioConfigs
    ? { ...defaultAvatarConfig.scenarioConfigs }
    : {}

  // 시나리오 설정이 없으면 기본 시나리오 반환
  if (!scenarioConfigs || scenarioConfigs.length === 0) {
    console.log(`[CustomAvatar] 시나리오 설정 없음 - ${bodyType} 기본 시나리오 사용`)
    return scenarios
  }

  // PlayMode 변환 함수
  const mapPlayMode = (mode: string): 'once' | 'random_loop' | 'last_loop' | 'sequential_loop' => {
    switch (mode) {
      case 'once': return 'once'
      case 'loop': return 'sequential_loop'
      case 'random': return 'random_loop'
      case 'last_loop': return 'last_loop'
      default: return 'once'
    }
  }

  // 2. 시나리오 설정으로 오버라이드
  for (const config of scenarioConfigs) {
    // 애니메이션 이름 목록 생성
    const animationNames: string[] = []

    // 순서대로 정렬
    const sortedAnims = [...config.animations].sort((a, b) => a.order - b.order)

    for (const animItem of sortedAnims) {
      if (animItem.isDefault) {
        // 기본 애니메이션: default_ 접두사 제거 후 사용
        // 예: default_input_processing01_a -> input_processing01_a
        const animName = animItem.animationId.replace(/^default_/, '')
        animationNames.push(animName)
      } else {
        // 라이브러리 애니메이션: lib_ 접두사 사용
        animationNames.push(`lib_${animItem.animationId}`)
      }
    }

    // 시나리오 오버라이드
    if (animationNames.length > 0) {
      scenarios[config.scenarioType] = {
        animations: animationNames,
        playMode: mapPlayMode(config.playMode),
        blendDuration: config.blendDuration,
        description: `시나리오 설정 기반 ${config.scenarioType}`,
      }
      console.log(`[CustomAvatar] 시나리오 '${config.scenarioType}' 설정 적용:`, animationNames)
    }
  }

  return scenarios
}

/**
 * 시나리오 설정 기반 리소스 배열 생성 (확장 버전)
 */
function createResourcesWithScenarioConfigs(data: CustomAvatarDataWithScenarios): ResourceType[] {
  const resources: ResourceType[] = [
    {
      name: 'custom_model',
      type: 'model_only',
      path: data.modelUrl,
      format: 'glb',
    },
  ]

  // 1. 등신별 기본 애니메이션 리소스 추가
  const defaultAnimResources = BODY_TYPE_DEFAULT_RESOURCES[data.bodyType]
  if (defaultAnimResources) {
    console.log(`[CustomAvatar] ${data.bodyType} 기본 애니메이션 로드`)
    resources.push(...defaultAnimResources)
  }

  // 2. 시나리오 설정에서 참조하는 라이브러리 애니메이션 추가
  if (data.scenarioConfigs && data.animationLibrary) {
    const libraryResources = createLibraryAnimationResources(
      data.scenarioConfigs,
      data.animationLibrary
    )
    console.log(`[CustomAvatar] 라이브러리 애니메이션 ${libraryResources.length}개 추가`)
    resources.push(...libraryResources)
  }

  // 3. 레거시 커스텀 애니메이션 추가 (하위 호환)
  if (data.animations && data.animations.length > 0 && !data.scenarioConfigs) {
    console.log(`[CustomAvatar] 레거시 커스텀 애니메이션 ${data.animations.length}개 추가`)
    for (const anim of data.animations) {
      resources.push({
        name: `custom_${anim.name}`,
        type: 'animation_only',
        path: anim.animationUrl,
        format: 'glb',
      })
    }
  }

  return resources
}

/**
 * 시나리오 설정 기반 AvatarConfig 생성 (확장 버전)
 */
export function createCustomAvatarConfigWithScenarios(data: CustomAvatarDataWithScenarios): AvatarConfig {
  const resources = createResourcesWithScenarioConfigs(data)

  // 시나리오 설정이 있으면 우선 사용
  let scenarios: ScenarioConfigs<string>
  if (data.scenarioConfigs && data.scenarioConfigs.length > 0 && data.animationLibrary) {
    scenarios = createScenariosFromConfigs(
      data.bodyType,
      data.scenarioConfigs,
      data.animationLibrary
    )
  } else {
    // 레거시: 기존 방식 사용
    scenarios = createCustomScenarios(data, resources)
  }

  const scale = data.scale || BODY_TYPE_SCALES[data.bodyType] || { x: 1, y: 1, z: 1 }

  // lipsync 설정
  let lipsync: LipsyncConfig
  const hasCustomLipsync = data.lipsyncConfig?.indices && data.lipsyncConfig.indices.length > 0

  if (hasCustomLipsync) {
    const lipsyncConfigDetail = data.lipsyncConfig.config as unknown as
      | RangeMappingConfig
      | FilterConfig
      | VrmFilterConfig
      | undefined

    lipsync = {
      indices: data.lipsyncConfig.indices,
      processingType: data.lipsyncConfig.processingType,
      config: lipsyncConfigDetail,
    }
  } else {
    const defaultAvatarConfig = BODY_TYPE_DEFAULT_AVATAR_CONFIGS[data.bodyType]
    if (defaultAvatarConfig?.lipsync) {
      lipsync = defaultAvatarConfig.lipsync
    } else {
      lipsync = {
        indices: [],
        processingType: 'filter',
        config: { indices: [] },
      }
    }
  }

  return {
    type: 'custom' as const,
    modelKey: 'custom_model',
    resources,
    scenarioConfigs: scenarios,
    scale,
    position: data.position,
    rotation: data.rotation,
    lipsync,
  }
}

/**
 * 시나리오 설정 기반 전체 Three.js 설정 생성 (확장 버전)
 */
export function createCustomAvatarThreeConfigWithScenarios(data: CustomAvatarDataWithScenarios): {
  loader: LoaderConfig
  camera: CameraConfig
  lighting: LightingConfig
  renderer: RendererConfig
  avatar: AvatarConfig
} {
  return {
    loader: defaultConfig.loader,
    camera: createCustomCameraConfig(data),
    lighting: createCustomLightingConfig(data),
    renderer: {
      ...defaultConfig.renderer,
      bloom: undefined,
    },
    avatar: createCustomAvatarConfigWithScenarios(data),
  }
}

/**
 * CustomAvatar 응답과 시나리오 설정을 CustomAvatarDataWithScenarios로 변환
 */
export function transformToCustomAvatarDataWithScenarios(
  avatar: {
    id: string
    name: string
    bodyType: string
    modelUrl: string
    thumbnailUrl?: string
    cameraConfig: Record<string, unknown>
    lightingConfig: Record<string, unknown>
    lipsyncConfig: Record<string, unknown>
    animations: Array<Record<string, unknown>>
    avatarTransform?: {
      scale: { x: number; y: number; z: number }
      position: { x: number; y: number; z: number }
      rotation: { x: number; y: number; z: number }
    }
  },
  scenarioConfigs?: Array<{
    id: string
    scenarioType: string
    animations: Array<{
      animationId: string
      isDefault: boolean
      order: number
    }>
    playMode: string
    blendDuration: number
  }>,
  animationLibrary?: Array<{
    id: string
    name: string
    bodyType: string
    animationUrl: string
  }>
): CustomAvatarDataWithScenarios {
  const baseData = transformToCustomAvatarData(avatar)

  return {
    ...baseData,
    scenarioConfigs: scenarioConfigs?.map(config => ({
      id: config.id,
      scenarioType: config.scenarioType,
      animations: config.animations.map(anim => ({
        animationId: anim.animationId,
        isDefault: anim.isDefault,
        order: anim.order,
      })),
      playMode: config.playMode as 'once' | 'loop' | 'random' | 'last_loop',
      blendDuration: config.blendDuration,
    })),
    animationLibrary: animationLibrary?.map(anim => ({
      id: anim.id,
      name: anim.name,
      bodyType: anim.bodyType,
      animationUrl: anim.animationUrl,
    })),
  }
}
